import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CommissionCard } from './CommissionCard';

interface Commission {
  id: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  deadline?: string;
  createdAt: string;
  patron?: {
    username: string;
    avatar: string | null;
  };
  creator?: {
    username: string;
    avatar: string | null;
  };
}

interface CommissionStats {
  active: number;
  completed: number;
  totalRevenue: number;
}

type ViewMode = 'client' | 'creator';

export const CommissionListPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [viewMode, setViewMode] = useState<ViewMode>('client');
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [stats, setStats] = useState<CommissionStats>({ active: 0, completed: 0, totalRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, [viewMode, statusFilter]);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: viewMode === 'client' ? 'requested' : 'received',
        ...(statusFilter !== 'ALL' && { status: statusFilter }),
        limit: '50'
      });

      const response = await fetch(`/api/commissions?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      setCommissions(data.commissions || []);
      
      // Calculate stats
      const activeCommissions = (data.commissions || []).filter((c: Commission) =>
        ['PENDING', 'ACCEPTED', 'IN_PROGRESS', 'DELIVERED'].includes(c.status)
      );
      const completedCommissions = (data.commissions || []).filter((c: Commission) =>
        ['COMPLETED', 'APPROVED'].includes(c.status)
      );
      const totalRevenue = completedCommissions.reduce((sum: number, c: Commission) => sum + c.amount, 0);

      setStats({
        active: activeCommissions.length,
        completed: completedCommissions.length,
        totalRevenue
      });
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCommissions = commissions.filter(commission =>
    searchQuery === '' ||
    commission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    commission.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Commissions</h1>
        <p className="text-gray-600">Manage your custom commission requests</p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Active</p>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Completed</p>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">{viewMode === 'creator' ? 'Revenue' : 'Spent'}</p>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-orange-600">${stats.totalRevenue.toLocaleString()}</p>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setViewMode('client')}
              className={`${
                viewMode === 'client'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              As Client
              {viewMode === 'client' && <span className="ml-2 bg-orange-100 text-orange-600 px-2 py-1 rounded-full text-xs">{commissions.length}</span>}
            </button>
            <button
              onClick={() => setViewMode('creator')}
              className={`${
                viewMode === 'creator'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              As Creator
              {viewMode === 'creator' && <span className="ml-2 bg-orange-100 text-orange-600 px-2 py-1 rounded-full text-xs">{commissions.length}</span>}
            </button>
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DELIVERED">Delivered</option>
              <option value="COMPLETED">Completed</option>
              <option value="APPROVED">Approved</option>
              <option value="DISPUTED">Disputed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search commissions..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* New Commission Button */}
      {viewMode === 'client' && (
        <div className="mb-6">
          <Link
            to="/commissions/new"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Request New Commission
          </Link>
        </div>
      )}

      {/* Commissions Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      ) : filteredCommissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {viewMode === 'client' ? 'No commission requests' : 'No commission offers'}
          </h3>
          <p className="text-gray-600 mb-6">
            {viewMode === 'client'
              ? "You haven't requested any commissions yet"
              : "You haven't received any commission requests yet"}
          </p>
          {viewMode === 'client' && (
            <Link
              to="/commissions/new"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              Request Your First Commission
            </Link>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCommissions.map((commission) => (
            <CommissionCard
              key={commission.id}
              commission={commission}
              viewAs={viewMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};
